import { notification } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLazyGetChainsForSystemQuery } from '@app/api/Chains';
import {
  useLazyGetUserProfileForViewQuery,
  useSendoutInviteLetterMutation,
  useUnarchiveUserMutation,
} from '@app/api/User/api';
import { SUCCESS, WARNING } from '@app/constants';
import {
  useMemoizeObject,
  useUserPrint,
  useUsersAccessRights,
} from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NSmallGlobalEntities, NUsers } from '@app/types';

export const useUserPreview = () => {
  const [user, setUser] = useState<NUsers.ModelUserProfile>();
  const [isLoading, setIsLoading] = useState(false);
  const [chains, setChains] = useState<NSmallGlobalEntities.ModelItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLetterSending, setIsLetterSending] = useState(false);

  const { userId, database, activeBranchId } = useUserPrint();

  const [searchParams] = useSearchParams();
  const editableUserId = Number(searchParams.get('id'));
  const activeTabKey = searchParams.get('key');

  const { showNotification } = useContext(NotificationContext);

  const [, contextHolder] = notification.useNotification();

  const { hasUserRightToEdit } = useUsersAccessRights();

  const memoizedUser = useMemoizeObject(user);

  const [unarchiveUser, { isLoading: isUnarchiveUserLoading }] =
    useUnarchiveUserMutation();
  const [getChainsForSystem] = useLazyGetChainsForSystemQuery();
  const [getUserProfileForView] = useLazyGetUserProfileForViewQuery();
  const [sendoutInviteLetter] = useSendoutInviteLetterMutation();

  useEffect(() => {
    const fetchData = async () => {
      const hasUserPrint = userId && database && activeBranchId;

      if (!hasUserPrint) return;

      try {
        setIsLoading(true);

        const result = await getUserProfileForView({
          ActiveBranchId: activeBranchId,
          profileUserId: editableUserId,
        }).unwrap();

        setUser(result);

        try {
          const chainsResult = await getChainsForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap();

          setChains(chainsResult);
        } catch (error) {
          showNotification((error as Error).message, WARNING);
        }
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    activeBranchId,
    database,
    userId,
    editableUserId,
    activeTabKey,
    memoizedUser,
    showNotification,
    getChainsForSystem,
    getUserProfileForView,
  ]);

  const sendLetter = async () => {
    setIsLetterSending(true);
    try {
      await sendoutInviteLetter({
        ActiveBranchId: activeBranchId,
        forUserId: editableUserId,
      }).unwrap();
      showNotification('Приглашение отправлено', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setIsLetterSending(false);
      setIsModalOpen(false);
    }
  };

  const restoreUser = async (id: number) => {
    try {
      await unarchiveUser({
        userId: id,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      const result = await getUserProfileForView({
        ActiveBranchId: activeBranchId,
        profileUserId: id,
      }).unwrap();

      setUser(result);

      showNotification('Восстановление прошло успешно', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  return {
    user,
    isLoading,
    contextHolder,
    chains,
    isModalOpen,
    setIsModalOpen,
    isLetterSending,
    sendLetter,
    hasUserRightToEdit,
    restoreUser,
    isUnarchiveUserLoading,
  };
};
